import isEmpty from 'lodash/isEmpty'
import { useCallback, useEffect } from 'react'
import { useReload } from '@hooks/reloadContext'
import api, { IPortfolioRegion, LocationWorkingHoursGroupI } from '@api'
import { Toaster } from '@ui'
import useLocations from '@ui/selects/LocationSelect/useLocations'
import { find, findIndex, update, difference } from 'lodash'
import { reject, some, compose } from 'lodash/fp'
import { getWorkingHoursGroupsByRegions } from '../utils'
import { IUseSettings } from './useSettings'
import { getDefaultTimes } from '@utils'

export type useWorkingHoursGroupsT = WorkingHoursGroupsDataT & {
  addNewWorkingHoursGroup: () => void
  removeWorkingHoursGroupById: (id: string) => void
  cancelWorkingHoursGroupEditingById: (id: string) => void
  editWorkingHoursGroupById: (id: string) => void
  saveWorkingHoursGroupById: (
    id: string,
    data: {
      floorIds: string[]
      timeFrom: string
      timeTo: string
    },
    onError?: () => void
  ) => void
  updateWorkingHoursGroupById: (id: string, data?: Partial<LocationWorkingHoursGroupI>) => void
  setWorkingHoursGroups: (locationWorkingHoursGroups: LocationWorkingHoursGroupI[]) => void
  regions: IPortfolioRegion[]
  allSelectedFloorIds: string[]
}

type WorkingHoursGroupsDataT = {
  bookingWorkdayTimeFrom: string
  bookingWorkdayTimeTo: string
  locationWorkingHoursGroups: LocationWorkingHoursGroupI[]
}

const useWorkingHoursLocationGroups = <Data extends WorkingHoursGroupsDataT>(
  data: Data,
  setData: IUseSettings['setData']
): useWorkingHoursGroupsT => {
  const regions = useLocations(false, true)
  const reload = useReload()

  const _getLocationWorkingHoursGroupById = (
    key: string
  ): LocationWorkingHoursGroupI | undefined => {
    return find(data.locationWorkingHoursGroups, { key })
  }

  const _updateLocationWorkingHoursGroupsById = (
    id: string,
    updatingObject: Partial<LocationWorkingHoursGroupI>
  ): LocationWorkingHoursGroupI[] => {
    const indexUpdatedGroup = findIndex(data.locationWorkingHoursGroups, { key: id })

    return update(data.locationWorkingHoursGroups, `[${indexUpdatedGroup}]`, (group) => ({
      ...group,
      ...updatingObject,
    }))
  }

  const _deleteWorkingHoursGroupById = (groupKey: string): void => {
    const locationWorkingHoursGroups = reject<LocationWorkingHoursGroupI>(
      { key: groupKey },
      data.locationWorkingHoursGroups
    )

    setWorkingHoursGroups(locationWorkingHoursGroups)
  }

  const _setWorkingHoursGroupsByRegions = useCallback((): void => {
    const groups = getWorkingHoursGroupsByRegions(regions)
    setWorkingHoursGroups(groups)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions])

  const setWorkingHoursGroups = (
    locationWorkingHoursGroups: LocationWorkingHoursGroupI[]
  ): void => {
    setData({ locationWorkingHoursGroups })
  }

  const addNewWorkingHoursGroup = () => {
    setWorkingHoursGroups([
      ...data.locationWorkingHoursGroups,
      {
        key: `location-group-${Date.now()}`,
        isBeingEdited: true,
        isLoading: false,
        isChanged: false,
        isNewItem: true,
        timeFrom: data.bookingWorkdayTimeFrom,
        timeTo: data.bookingWorkdayTimeTo,
        floorIds: [],
      },
    ])
  }

  const removeWorkingHoursGroupById = async (groupKey: string): Promise<void> => {
    const groupItem = _getLocationWorkingHoursGroupById(groupKey)

    _deleteWorkingHoursGroupById(groupKey)

    try {
      if (!isEmpty(groupItem?.floorIds)) {
        await api.building.floor.removeFloorSpecificBusinessHours({
          floorIds: groupItem?.floorIds || [],
        })
      }

      Toaster.success({ message: 'Location group has been deleted' })
    } catch (error: any) {
      Toaster.fail({ error })
    }
  }

  const cancelWorkingHoursGroupEditingById = (key: string) => {
    const item = _getLocationWorkingHoursGroupById(key)

    if (item?.isNewItem) {
      _deleteWorkingHoursGroupById(key)
      return
    }

    const locationWorkingHoursGroups = _updateLocationWorkingHoursGroupsById(key, {
      isBeingEdited: false,
      isChanged: false,
    })

    setWorkingHoursGroups(locationWorkingHoursGroups)
  }

  const editWorkingHoursGroupById = (key: string) => {
    const locationWorkingHoursGroups = _updateLocationWorkingHoursGroupsById(key, {
      isBeingEdited: true,
      isChanged: false,
    })

    setWorkingHoursGroups(locationWorkingHoursGroups)
  }

  const showGroupAlreadyExistToast = (updatingObject: {
    floorIds: string[]
    timeFrom: string
    timeTo: string
  }) => {
    const { timeFrom, timeTo } = getDefaultTimes(updatingObject.timeFrom, updatingObject.timeTo)

    Toaster.fail({
      message: `
        Location group with ${timeFrom.format('h:mm a')} - ${timeTo.format(
        'h:mm a'
      )} already exists`,
    })
  }

  const saveWorkingHoursGroupById: useWorkingHoursGroupsT['saveWorkingHoursGroupById'] = async (
    groupKey,
    updatingObject,
    onErrorCallBack
  ): Promise<void> => {
    const isGroupExisting = compose(
      some({
        timeFrom: updatingObject.timeFrom,
        timeTo: updatingObject.timeTo,
      }),
      reject({ key: groupKey })
    )(data.locationWorkingHoursGroups)

    if (isGroupExisting) {
      showGroupAlreadyExistToast(updatingObject)
      editWorkingHoursGroupById(groupKey)
      return
    }

    const groupItem = _getLocationWorkingHoursGroupById(groupKey)
    const locationWorkingHoursGroups = _updateLocationWorkingHoursGroupsById(groupKey, {
      ...updatingObject,
      isLoading: true,
      isNewItem: false,
      isChanged: false,
    })
    setWorkingHoursGroups(locationWorkingHoursGroups)

    const updatedGroupItem = _getLocationWorkingHoursGroupById(groupKey)
    if (!updatedGroupItem) return

    const floorIds = updatedGroupItem?.floorIds || []
    const removingIds = difference(groupItem?.floorIds || [], floorIds)

    try {
      await api.building.floor.addFloorsSpecificBusinessHour({
        floorIds: floorIds,
        bookingWorkdayTimeFrom: updatedGroupItem?.timeFrom || null,
        bookingWorkdayTimeTo: updatedGroupItem?.timeTo || null,
      })

      if (!isEmpty(removingIds)) {
        await api.building.floor.removeFloorSpecificBusinessHours({
          floorIds: removingIds,
        })
      }

      reload.buildings()

      Toaster.success({ message: 'Saved' })
    } catch (error: any) {
      onErrorCallBack?.()
      Toaster.fail({ error })
    } finally {
      const locationWorkingHoursGroups = _updateLocationWorkingHoursGroupsById(groupKey, {
        isLoading: false,
        isBeingEdited: false,
      })

      setWorkingHoursGroups(locationWorkingHoursGroups)
    }
  }

  const updateWorkingHoursGroupById = (
    id: string,
    updatingObject?: Partial<LocationWorkingHoursGroupI>
  ) => {
    const locationWorkingHoursGroups = _updateLocationWorkingHoursGroupsById(id, {
      ...updatingObject,
      isChanged: true,
    })
    setWorkingHoursGroups(locationWorkingHoursGroups)
  }

  useEffect(() => {
    _setWorkingHoursGroupsByRegions()
  }, [_setWorkingHoursGroupsByRegions])

  const allSelectedFloorIds = data.locationWorkingHoursGroups.reduce<string[]>(
    (floorIds, group) => [...floorIds, ...group.floorIds],
    []
  )

  return {
    regions,
    allSelectedFloorIds,
    addNewWorkingHoursGroup,
    removeWorkingHoursGroupById,
    cancelWorkingHoursGroupEditingById,
    editWorkingHoursGroupById,
    saveWorkingHoursGroupById,
    updateWorkingHoursGroupById,
    setWorkingHoursGroups,
    bookingWorkdayTimeFrom: data.bookingWorkdayTimeFrom,
    bookingWorkdayTimeTo: data.bookingWorkdayTimeTo,
    locationWorkingHoursGroups: data.locationWorkingHoursGroups,
  }
}

export default useWorkingHoursLocationGroups
